import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Save, X, RefreshCw, AlertTriangle, CheckCircle, Undo2 } from "lucide-react";
import { cn } from "../../lib/utils";

// ─── Sticky save/cancel bar + cancel confirmation dialog ─────────────────────

const CompanySaveBar = ({
  isEditing,
  hasUnsavedChanges,
  loading,
  isValid,
  handleCancel,
  handleSave,
  showCancelDialog,
  setShowCancelDialog,
  cancelEditing,
}) => {
  const { t } = useTranslation();

  if (!isEditing) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40" style={{ backgroundColor: "rgba(1,1,2,0.92)", backdropFilter: "blur(12px)", borderTop: "1px solid var(--lm-border)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-3 text-sm font-medium">
              {hasUnsavedChanges ? (
                <>
                  <div className="p-1.5 bg-orange-900/30 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-orange-400" />
                  </div>
                  <span className="text-orange-400">{t("companyConfig.messages.unsavedChanges")}</span>
                </>
              ) : (
                <>
                  <div className="p-1.5 bg-green-900/30 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  </div>
                  <span className="text-green-400">{t("companyConfig.messages.noChanges")}</span>
                </>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: hasUnsavedChanges ? "#f0a500" : "var(--lm-ink-2)",
                  backgroundColor: "var(--lm-s1)",
                  border: `1px solid ${hasUnsavedChanges ? "rgba(240,165,0,0.30)" : "var(--lm-border)"}`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--lm-ink)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = hasUnsavedChanges ? "#f0a500" : "var(--lm-ink-2)"; }}
              >
                <X className="mr-2 h-4 w-4" />
                {t("companyConfig.actions.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={loading || !isValid}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#5e6ad2", color: "#ffffff", border: "none" }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#828fff"; }}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {loading ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                {t("companyConfig.actions.save")}
                {hasUnsavedChanges && (
                  <span className="ml-1 text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>•</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-3 text-xl font-bold text-foreground">
              <div className="p-2 bg-orange-900/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-400" />
              </div>
              <span>{t("companyConfig.dialogs.cancelTitle")}</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-muted-foreground leading-relaxed">
              {t("companyConfig.dialogs.cancelDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="space-x-3">
            <AlertDialogCancel>
              {t("companyConfig.dialogs.continueEditing")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelEditing}
              className="text-white transition-colors"
              style={{ backgroundColor: "#e5484d" }}
            >
              <Undo2 className="mr-2 h-4 w-4" />
              {t("companyConfig.dialogs.discardChanges")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CompanySaveBar;
